import type {
  GetAstroRootCommandFn,
  GetRootCommandFn,
} from '@wix/cli-command-contract';
import type { DefinedWixConfig } from '@wix/cli-plugin-core';
import { WIX_CONFIG_FILENAME } from '@wix/cli-core-definitions';
import { fields, variant } from 'variant';
import { importCliApp } from './cli-app.js';
import { importCliAstro } from './cli-astro.js';
import { importCliSite } from './cli-site.js';
import { isWixConfigExists, readWixConfig } from './files/wix.config.js';

const ProgramFlow = variant({
  APP: fields<{ getRootCommand: GetRootCommandFn }>(),
  ASTRO: fields<{
    definedWixConfig: DefinedWixConfig;
    getRootCommand: GetAstroRootCommandFn;
  }>(),
  SITE: fields<{ getRootCommand: GetRootCommandFn }>(),
  UNKNOWN: fields<{ reason: string }>(),
});

const loadLegacyConfig = async (projectFolder: string) => {
  const isLegacyConfigExist = await isWixConfigExists(projectFolder);

  if (!isLegacyConfigExist) {
    return;
  }

  return readWixConfig(projectFolder);
};

export async function identifyProgramFlow(projectFolder: string) {
  const legacyConfig = await loadLegacyConfig(projectFolder);

  const importedAstro = await importCliAstro(projectFolder, legacyConfig);
  if (importedAstro) {
    const { astro, definedWixConfig } = importedAstro;
    return ProgramFlow.ASTRO({
      definedWixConfig,
      getRootCommand: astro.getRootCommand,
    });
  }

  if (!legacyConfig) {
    return ProgramFlow.UNKNOWN({
      reason: `Project type could not be determined due to a missing configuration file "${WIX_CONFIG_FILENAME}".`,
    });
  }

  const app = await importCliApp(legacyConfig);
  if (app) {
    return ProgramFlow.APP({ getRootCommand: app.getRootCommand });
  }

  const site = await importCliSite(legacyConfig);
  if (site) {
    return ProgramFlow.SITE({ getRootCommand: site.getRootCommand });
  }

  return ProgramFlow.UNKNOWN({
    reason:
      'A configuration file was found, but it is either malformed or missing required fields, preventing project type identification.',
  });
}
